import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelectedContract, useSelectedCampaign } from 'hooks'
import { MenuTitles, Pages } from 'constants/menu'
import { ContractSubPage } from '../ContractSubPage'
import { ErrorModal } from 'helpers/ErrorModal'
import { isDirtyEqual } from 'utils/compares'
import { getContractInformationSubPages } from './ContractInformationSubPages'
import { SubPagePanel } from 'components/panel/SubPagePanel'
import { AgxRow, AgxTypography } from '@urbanx/agx.ui-components2'
import {
  CampaignDetailModelState,
  Deposit,
  DepositType,
  StaffUpdateContractDepositsRequest,
  useInvalidateUseStaffGetContractDeposits,
  useStaffGetContractDeposits,
  useStaffUpdateContractDeposits,
} from '@urbanx/agx.campaigns.client'
import { DepositPanel } from './panels/DepositPanel'
import { AddDepositButton } from './modals/AddDepositButton'
import LoadingState from 'components/loadingState/LoadingState'
import RexDisconnectedModal from 'pages/campaigns/components/manageCampaign/RexDisconnectedModal'
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { RexDisconnectedModalFields, RexDisconnectedModalFieldValue } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper'
import { isEqual } from 'lodash-es'
import { useFormConditionDueDate } from './components/useFormConditionDueDate'

const Deposits = () => {
  const { contract } = useSelectedContract()
  const { campaign } = useSelectedCampaign()
  const isNsw = campaign?.state === CampaignDetailModelState.NSW

  const [isLoaded, setIsLoaded] = useState(false)
  const [, setValidate] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([])
  const invalidateUseStaffGetContractDeposits =
    useInvalidateUseStaffGetContractDeposits()

  const {
    data: contractDeposits,
    isPending,
    isError,
  } = useStaffGetContractDeposits(
    contract?.contractId || '',
    !!contract?.contractId
  )

  const [initialDeposit, setInitialDeposit] = useState<Deposit | undefined>(
    contractDeposits?.deposits?.find(
      (d) => d.depositType === DepositType.Initial
    )
  )

  const [balanceDeposit, setBalanceDeposit] = useState<Deposit | undefined>(
    contractDeposits?.deposits?.find(
      (d) => d.depositType === DepositType.Balance
    )
  )

  const [openRexDisconnectedModal, setOpenRexDisconnectedModal] = useState(false)
  const [rexModalChangedFields, setRexModalChangedFields] = useState<Map<RexDisconnectedModalFields, RexDisconnectedModalFieldValue>>(new Map())

  useEffect(() => {
    setInitialDeposit(
      contractDeposits?.deposits?.find(
        (d) => d.depositType === DepositType.Initial
      )
    )
    setBalanceDeposit(
      contractDeposits?.deposits?.find(
        (d) => d.depositType === DepositType.Balance
      )
    )
    setIsLoaded(!isPending)
  }, [contractDeposits, isPending])

  useEffect(() => {
    setValidate(
      !isDirtyEqual(
        initialDeposit,
        contractDeposits?.deposits?.find(
          (d) => d.depositType === DepositType.Initial
        )
      ) ||
      !isDirtyEqual(
        balanceDeposit,
        contractDeposits?.deposits?.find(
          (d) => d.depositType === DepositType.Balance
        )
      )
    )
  }, [initialDeposit, balanceDeposit])

  useEffect(() => {
    if (!openRexDisconnectedModal) {
      setRexModalChangedFields(new Map());
    }
  }, [openRexDisconnectedModal]);

  const { staffUpdateContractDeposits, staffUpdateContractDepositsPending } =
    useStaffUpdateContractDeposits({
      onSuccess: () => {},
      onError: () => {},
    })

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '', campaign?.campaignId !== undefined);

  const isFormDirty = useMemo(() => {
    return (
      !isDirtyEqual(
        initialDeposit,
        contractDeposits?.deposits?.find(
          (d) => d.depositType === DepositType.Initial
        )
      ) ||
      !isDirtyEqual(
        balanceDeposit,
        contractDeposits?.deposits?.find(
          (d) => d.depositType === DepositType.Balance
        )
      )
    )
  }, [initialDeposit, balanceDeposit, contractDeposits])

  const prevInitialDeposit = useMemo(
    () => contractDeposits?.deposits?.find((d) => d.depositType === DepositType.Initial),
    [contractDeposits]
  )
  const prevBalanceDeposit = useMemo(
    () => contractDeposits?.deposits?.find((d) => d.depositType === DepositType.Balance),
    [contractDeposits]
  )

  // Calculate due dates using the hook
  const prevInitialDueDate = useFormConditionDueDate(prevInitialDeposit)
  const currentInitialDueDate = useFormConditionDueDate(initialDeposit)
  const prevBalanceDueDate = useFormConditionDueDate(prevBalanceDeposit)
  const currentBalanceDueDate = useFormConditionDueDate(balanceDeposit)

  const onSave = useCallback(
    async (success: () => void) => {
      if (!contract?.contractId) return

      if (!rexListing && isPendingRexListing === false) {
        setRexModalChangedFields(prev => {
          const newMap = new Map(prev)

          // Initial Deposit amount
          if (!isEqual(prevInitialDeposit?.amount, initialDeposit?.amount)) {
            newMap.set(RexDisconnectedModalFields.InitialDeposit, initialDeposit?.amount)
          }
          // Initial Deposit due date
          if (!isEqual(prevInitialDueDate, currentInitialDueDate)) {
            newMap.set(RexDisconnectedModalFields.InitialDepositDueDate, currentInitialDueDate.dueDate?.toLocaleDateString() || '')
          }
          // Balance Deposit amount
          if (!isEqual(prevBalanceDeposit?.amount, balanceDeposit?.amount)) {
            newMap.set(RexDisconnectedModalFields.BalanceDeposit, balanceDeposit?.amount)
          }
          // Balance Deposit due date
          if (!isEqual(prevBalanceDueDate, currentBalanceDueDate)) {
            newMap.set(RexDisconnectedModalFields.BalanceDepositDueDate, currentBalanceDueDate.dueDate?.toLocaleDateString() || '')
          }

          return newMap
        })

        if (
          !isEqual(prevInitialDeposit?.amount, initialDeposit?.amount) ||
          !isEqual(prevInitialDueDate, currentInitialDueDate) ||
          !isEqual(prevBalanceDeposit?.amount, balanceDeposit?.amount) ||
          !isEqual(prevBalanceDueDate, currentBalanceDueDate)
        ) {
          setOpenRexDisconnectedModal(true)
        }
      }

      const request: StaffUpdateContractDepositsRequest = {
        deposits: [initialDeposit, balanceDeposit].filter(Boolean) as Deposit[],
      }

      await staffUpdateContractDeposits(
        {
          body: request,
          contractId: contract.contractId,
        },
        {
          onSuccess: async (data) => {
            if (data.success) {
              success()
              await invalidateUseStaffGetContractDeposits(contract.contractId)
            } else {
              setSubmitFormErrors(data.errors || [])
              setOpenErrorModal(true)
            }
          },
        }
      )
    },
    [
      initialDeposit,
      balanceDeposit,
      contract?.contractId,
      staffUpdateContractDeposits,
    ]
  )

  if (!isLoaded) {
    return <LoadingState />
  }

  return (
    <ContractSubPage
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.DEPOSITS}
      subPages={getContractInformationSubPages()}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateContractDepositsPending}
      isError={isError}
    >
      {!isPending ? (
        <>
          {!initialDeposit && !balanceDeposit?.isDepositSelected && (
            <SubPagePanel expandable={false}>
              <AgxRow
                gap="spacing-md"
                alignItems="alignCenter"
                justifyContent="center"
                fill
                padding={{ top: 'spacing-8xl', bottom: 'spacing-8xl' }}
              >
                <AgxTypography
                  id="no-deposits"
                  type="body_ui"
                  size="medium"
                  color=""
                >
                  No deposits added.
                </AgxTypography>
              </AgxRow>
            </SubPagePanel>
          )}
          {initialDeposit && (
            <DepositPanel
              id="initial-deposit-panel"
              panelTitle="Initial Deposit"
              deposit={initialDeposit}
              onChange={(value) =>
                setInitialDeposit(value as Deposit | undefined)
              }
            />
          )}
          {balanceDeposit && balanceDeposit.isDepositSelected && (
            <DepositPanel
              id="balance-deposit-panel"
              panelTitle="Balance Deposit"
              deposit={balanceDeposit}
              onChange={(value) =>
                setBalanceDeposit(value as Deposit | undefined)
              }
            />
          )}
          <AgxRow gap="spacing-md" justifyContent="left" alignItems="flex-start">
            {(!initialDeposit ||
              (isNsw && (!initialDeposit || !balanceDeposit))) && (
                <AddDepositButton
                  id="initial-deposit"
                  label={isNsw ? 'Add Deposit' : 'Add Initial Deposit'}
                  depositType={DepositType.Initial}
                  onAddDeposits={(initial, balance) => {
                    setInitialDeposit(initial)
                    if (isNsw || balance) {
                      setBalanceDeposit(balance)
                    }
                  }}
                />
              )}
            {!isNsw &&
              (!balanceDeposit || !balanceDeposit.isDepositSelected) && (
                <AddDepositButton
                  id="balance-deposit"
                  label="Add Balance Deposit"
                  depositType={DepositType.Balance}
                  onAddDeposits={(_, balance) => {
                    if (balance) {
                      setBalanceDeposit(balance)
                    }
                  }}
                  disabled={!initialDeposit}
                />
              )}
          </AgxRow>
        </>
      ) : undefined}
      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
      {openRexDisconnectedModal ? (
        <RexDisconnectedModal
          setIsDialogOpen={setOpenRexDisconnectedModal}
          changedFields={rexModalChangedFields}
        />
      ) : (
        <></>
      )}
    </ContractSubPage>
  )
}

export default Deposits
